import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from "idb";
import type {
  LibraryData,
  PermissionLevel,
  Schedule,
  TravelRouteResult,
  Trip,
  TripLeg,
  TripSummaryWithPermission,
} from "@poi-planner/shared";

/**
 * Offline Store
 *
 * The copy of the signed-in user's trips kept on this device so they can be
 * opened read-only without a connection. It is written directly by the app
 * (never through the service worker) so it stays scoped to one user, is
 * wiped on sign-out, and can be checked against trip revisions.
 *
 * Every function degrades to a no-op when IndexedDB is unavailable (private
 * browsing, tests): offline access is a convenience, never a requirement.
 */

const DB_NAME = "ppg-offline";
const DB_VERSION = 1;
/** Bump when the saved record shapes change; old copies are wiped. */
export const OFFLINE_SCHEMA_VERSION = 1;

export interface OfflineMeta {
  userId: string;
  schemaVersion: number;
  appVersion: string;
  lastSyncedAt: string | null;
}

export type SavedTravelRoute = [
  key: string,
  route: Omit<TravelRouteResult, "key">,
];

export interface SavedTrip {
  trip: Trip;
  schedule: Schedule;
  legs: TripLeg[];
  permission: PermissionLevel | "owner";
  pinnedPoiIds: string[];
  /** Resolved Google Routes durations, so travel pins show real times. */
  travelRoutes?: SavedTravelRoute[];
  savedAt: string;
}

interface OfflineDB extends DBSchema {
  meta: { key: "meta"; value: OfflineMeta };
  tripsList: { key: "list"; value: TripSummaryWithPermission[] };
  trips: { key: string; value: SavedTrip };
  library: { key: "library"; value: LibraryData };
}

let dbPromise: Promise<IDBPDatabase<OfflineDB> | null> | null = null;

function open(): Promise<IDBPDatabase<OfflineDB> | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (!dbPromise) {
    dbPromise = openDB<OfflineDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        db.createObjectStore("meta");
        db.createObjectStore("tripsList");
        db.createObjectStore("trips");
        db.createObjectStore("library");
      },
      // Another tab is deleting the database (sign-out): let it.
      blocking() {
        void closeDb();
      },
    }).catch((error: unknown) => {
      console.warn("Offline storage unavailable", error);
      dbPromise = null;
      return null;
    });
  }
  return dbPromise;
}

async function closeDb(): Promise<void> {
  const pending = dbPromise;
  dbPromise = null;
  (await pending)?.close();
}

/** Run an IndexedDB operation, logging and swallowing storage failures. */
async function withDb<T>(
  fallback: T,
  run: (db: IDBPDatabase<OfflineDB>) => Promise<T>,
): Promise<T> {
  try {
    const db = await open();
    return db ? await run(db) : fallback;
  } catch (error: unknown) {
    console.warn("Offline storage operation failed", error);
    return fallback;
  }
}

export function getMeta(): Promise<OfflineMeta | null> {
  return withDb(null, async (db) => (await db.get("meta", "meta")) ?? null);
}

/**
 * Make the saved copy belong to `userId`, wiping it first when it belongs to
 * someone else or uses an older record shape.
 */
export function prepareForUser(userId: string): Promise<boolean> {
  return withDb(false, async (db) => {
    const meta = await db.get("meta", "meta");
    if (
      meta?.userId === userId &&
      meta.schemaVersion === OFFLINE_SCHEMA_VERSION
    ) {
      return true;
    }
    const tx = db.transaction(
      ["meta", "tripsList", "trips", "library"],
      "readwrite",
    );
    await Promise.all([
      tx.objectStore("tripsList").clear(),
      tx.objectStore("trips").clear(),
      tx.objectStore("library").clear(),
      tx.objectStore("meta").put(
        {
          userId,
          schemaVersion: OFFLINE_SCHEMA_VERSION,
          appVersion: __APP_VERSION__,
          lastSyncedAt: null,
        },
        "meta",
      ),
    ]);
    await tx.done;
    return true;
  });
}

export function setLastSyncedAt(lastSyncedAt: string): Promise<void> {
  return withDb(undefined, async (db) => {
    const meta = await db.get("meta", "meta");
    if (!meta) return;
    await db.put(
      "meta",
      { ...meta, appVersion: __APP_VERSION__, lastSyncedAt },
      "meta",
    );
  });
}

export function saveTripsList(
  trips: TripSummaryWithPermission[],
): Promise<void> {
  return withDb(undefined, async (db) => {
    await db.put("tripsList", trips, "list");
  });
}

export function getTripsList(): Promise<TripSummaryWithPermission[] | null> {
  return withDb(
    null,
    async (db) => (await db.get("tripsList", "list")) ?? null,
  );
}

export function saveLibrary(library: LibraryData): Promise<void> {
  return withDb(undefined, async (db) => {
    await db.put("library", library, "library");
  });
}

export function getLibrary(): Promise<LibraryData | null> {
  return withDb(
    null,
    async (db) => (await db.get("library", "library")) ?? null,
  );
}

export function saveTrip(record: SavedTrip): Promise<void> {
  return withDb(undefined, async (db) => {
    await db.put("trips", record, record.trip.id);
  });
}

export function getTrip(tripId: string): Promise<SavedTrip | null> {
  return withDb(null, async (db) => (await db.get("trips", tripId)) ?? null);
}

export function getAllTrips(): Promise<SavedTrip[]> {
  return withDb([], (db) => db.getAll("trips"));
}

/** Delete saved trips that are no longer in the user's list. */
export function pruneTrips(keepIds: Iterable<string>): Promise<void> {
  const keep = new Set(keepIds);
  return withDb(undefined, async (db) => {
    const tx = db.transaction("trips", "readwrite");
    const ids = await tx.store.getAllKeys();
    await Promise.all(
      ids.filter((id) => !keep.has(id)).map((id) => tx.store.delete(id)),
    );
    await tx.done;
  });
}

/** Delete the whole saved copy (sign-out). */
export async function clearAll(): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  await closeDb();
  try {
    await deleteDB(DB_NAME);
  } catch (error: unknown) {
    console.warn("Could not delete offline storage", error);
  }
}
